import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { select, Selection } from 'd3-selection';
import { zoom as d3Zoom, zoomIdentity, zoomTransform, ZoomBehavior, ZoomTransform } from 'd3-zoom';
import { drag as d3Drag } from 'd3-drag';
import { forceSimulation, forceLink, forceManyBody, forceCenter, forceCollide, forceX, forceY, Simulation } from 'd3-force';
import { getCSSVar, getNodeDimensions, groupEdgesBySourceTarget } from '../../utils/erd.utils';
import { useDimensions } from './hooks/useDimensions';
import { D3ERDProps, D3Node, EdgeRenderConfig, ERDEdge, ERDMode } from '../../types/erd';
import { renderEdge, renderSelfLoop } from './edgeRenderers';
import { useZoomControls } from './hooks/useZoomControls';
import { useNodeTypeFilter } from './hooks/useNodeTypeFilter';
import './D3EntityRelationDiagram.css';
import { ZoomControls } from './ZoomControls';

interface D3DiagramProps extends D3ERDProps {
  mode: ERDMode;
  onNodesChange?: (nodes: D3Node[]) => void;
}

export const D3EntityRelationDiagram: React.FC<D3DiagramProps> = ({ nodes: erdNodes, edges: erdEdges, mode, onNodesChange }) => {
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [selectedEdges, setSelectedEdges] = useState<ERDEdge[]>([]);
  const [isClosingInfoPanel, setIsClosingInfoPanel] = useState(false);

  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const nodesRef = useRef<D3Node[]>([]);
  const selectedNodeIdRef = useRef<string | null>(null);
  const simulationRef = useRef<Simulation<D3Node, undefined> | null>(null);
  const containerGRef = useRef<Selection<SVGGElement, unknown, null, undefined> | null>(null);
  const edgeContainerRef = useRef<Selection<SVGGElement, unknown, null, undefined> | null>(null);
  const nodeContainerRef = useRef<Selection<SVGGElement, unknown, null, undefined> | null>(null);
  const zoomBehaviorRef = useRef<ZoomBehavior<SVGSVGElement, unknown> | null>(null);
  const initialTransformRef = useRef<ZoomTransform | null>(null);
  const initialNodePositionsRef = useRef<Map<string, { x: number; y: number }>>(new Map());
  const infoPanelCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { dimensions, debouncedDimensions } = useDimensions(containerRef);

  // Read CSS variables from the document root for styling
  const cssColors = useMemo(() => ({
    EDGE_COLOR: getCSSVar('--erd-edge-color') || '#C7A1F7',
    BADGE_COLOR: getCSSVar('--erd-badge-color') || '#9F7AEA',
    HIGHLIGHT_COLOR: getCSSVar('--erd-highlight-color') || '#632EA7',
    UNHIGHLIGHTED_COLOR: getCSSVar('--erd-unhighlighted-color') || '#DFDFDF',
    FONT_FAMILY: getCSSVar('--erd-font-family') || 'Inter, sans-serif',
  }), []);

  const { EDGE_COLOR, BADGE_COLOR, HIGHLIGHT_COLOR, UNHIGHLIGHTED_COLOR, FONT_FAMILY } = cssColors;

  const edgeRenderConfig: EdgeRenderConfig = useMemo(() => ({
    EDGE_COLOR,
    BADGE_COLOR,
    HIGHLIGHT_COLOR,
    UNHIGHLIGHTED_COLOR,
    FONT_FAMILY,
    selectedNodeIdRef,
    setSelectedEdges,
    setSelectedNodeId
  }), [EDGE_COLOR, BADGE_COLOR, HIGHLIGHT_COLOR, UNHIGHLIGHTED_COLOR, FONT_FAMILY]);

  const nodeTypes = useMemo(() => erdNodes.map((node) => ({
        id: node.id,
        label: node.label,
        color: node.background,
        textColor: node.color,
      })),
    [erdNodes],
  );

  const { visibleTypes, visibleTypesRef } = useNodeTypeFilter(nodeTypes);

  const renderEdgesOnly = useCallback(() => {
    if (!edgeContainerRef.current) { return; }

    const edgeGroupsContainer = edgeContainerRef.current;
    edgeGroupsContainer.selectAll('*').remove();

    const nodes = nodesRef.current;
    const currentVisibleTypes = visibleTypesRef.current;

    const visibleEdges = erdEdges.filter(
      (edge) => currentVisibleTypes.has(edge.source) && currentVisibleTypes.has(edge.target),
    );

    const groupedEdges = groupEdgesBySourceTarget(visibleEdges);

    groupedEdges.forEach((group) => {
      const sourceNode = nodes.find((node) => node.id === group.source);
      const targetNode = nodes.find((node) => node.id === group.target);
      const hasSourceCoords = !!sourceNode && Number.isFinite(sourceNode.x) && Number.isFinite(sourceNode.y);
      const hasTargetCoords = !!targetNode && Number.isFinite(targetNode.x) && Number.isFinite(targetNode.y);

      if (!hasSourceCoords || !hasTargetCoords) { return; }

      const isSelfLoop = group.source === group.target;
      const edgeCount = group.edges.length;
      const edgeGroup = edgeGroupsContainer.append('g').attr('class', 'edge-group');
      const isOutgoing = selectedNodeIdRef.current ? group.source === selectedNodeIdRef.current : false;
      const isIncoming = selectedNodeIdRef.current ? group.target === selectedNodeIdRef.current : false;

      if (isSelfLoop) {
        const nodeDims = getNodeDimensions(sourceNode.label);
        renderSelfLoop(edgeGroup, sourceNode, nodeDims.width, nodeDims.height, edgeCount, group.edges, edgeRenderConfig, isOutgoing, isIncoming);
      } else {
        const reverseKey = `${group.target}->${group.source}`;
        const hasReverse = groupedEdges.has(reverseKey);
        const sourceDims = getNodeDimensions(sourceNode.label);
        const targetDims = getNodeDimensions(targetNode.label);
        const avgRadius = (sourceDims.radius + targetDims.radius) / 2;

        renderEdge(edgeGroup, sourceNode, targetNode, avgRadius, edgeCount, hasReverse, group.edges, edgeRenderConfig, isOutgoing, isIncoming);
      }
    });
  }, [erdEdges, edgeRenderConfig]);

  const {  handleZoomIn, handleZoomOut, handleResetToInitial, handleResetZoom } = useZoomControls({
    svgRef,
    zoomBehaviorRef,
    initialTransformRef,
    nodeContainerRef,
    nodesRef,
    initialNodePositionsRef,
    simulationRef,
    visibleTypesRef,
    dimensions,
    renderEdgesOnly,
  });

  // Handle info panel close with animation
  const handleCloseInfoPanel = (): void => {
    setIsClosingInfoPanel(true);
    // Clear any existing timeout before starting a new one
    if (infoPanelCloseTimeoutRef.current !== null) {
      clearTimeout(infoPanelCloseTimeoutRef.current);
    }
    infoPanelCloseTimeoutRef.current = setTimeout(() => {
      setSelectedNodeId(null);
      setSelectedEdges([]);
      setIsClosingInfoPanel(false);
      infoPanelCloseTimeoutRef.current = null;
    }, 200); // Match animation duration
  };

  useEffect(() => {
    selectedNodeIdRef.current = selectedNodeId;
    if (nodesRef.current.length > 0) {
      renderEdgesOnly();
    }
  }, [selectedNodeId, renderEdgesOnly]);

  // Clear any pending info panel close timeout on unmount
  useEffect(() => {
    return () => {
      if (infoPanelCloseTimeoutRef.current !== null) {
        clearTimeout(infoPanelCloseTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!svgRef.current || !containerRef.current) { return; }

    // Skip rendering if dimensions haven't been measured yet (still using defaults)
    // This prevents double-render: once with defaults, once with actual size
    const { width, height } = debouncedDimensions;
    const actualWidth = containerRef.current.clientWidth;
    const actualHeight = containerRef.current.clientHeight;

    // If dimensions are significantly different from actual, skip this render
    // (wait for useDimensions to update with real values)
    if (Math.abs(width - actualWidth) > 50 || Math.abs(height - actualHeight) > 50) {
      return;
    }

    const svg = select(svgRef.current);
    svg.selectAll('*').remove();
    svg
      .style('font-family', FONT_FAMILY)
      .style('font-size', getCSSVar('--erd-font-size-medium') || '0.75rem');

    const nodes: D3Node[] = erdNodes.map((node, idx) => {
      const angle = (idx / erdNodes.length) * 2 * Math.PI;
      const radius = Math.min(width, height) * 0.35;
      return {
        ...node,
        x: width / 2 + Math.cos(angle) * radius,
        y: height / 2 + Math.sin(angle) * radius,
      };
    });
    nodesRef.current = nodes;

    const zoom = d3Zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.1, 4])
      .on('zoom', (event) => {
        if (containerGRef.current) {
          containerGRef.current.attr('transform', event.transform.toString());
        }
        // Close any open badge tooltip modals when zooming/panning
        select('.badge-tooltip-popup').remove();
        select('body').on('click.badge-tooltip', null);
      });

    svg.call(zoom);
    zoomBehaviorRef.current = zoom;

    const initialScale = 0.8;
    const initialTransform = zoomIdentity
      .translate((width * (1 - initialScale)) / 2, (height * (1 - initialScale)) / 2)
      .scale(initialScale);
    svg.transition().duration(0).call(zoom.transform, initialTransform);

    const container = svg.append('g');
    containerGRef.current = container;
    container.attr('transform', initialTransform.toString());

    const defs = svg.append('defs');

    defs
      .append('marker')
      .attr('id', 'arrow')
      .attr('viewBox', '0 -5 10 10')
      .attr('refX', 8)
      .attr('refY', 0)
      .attr('markerWidth', 6)
      .attr('markerHeight', 6)
      .attr('orient', 'auto')
      .append('path')
      .attr('d', 'M0,-4L10,0L0,4Z')
      .attr('fill', EDGE_COLOR);

    // Highlight arrow for selected edges (both incoming and outgoing)
    defs
      .append('marker')
      .attr('id', 'arrow-highlight')
      .attr('viewBox', '0 -5 10 10')
      .attr('refX', 8)
      .attr('refY', 0)
      .attr('markerWidth', 6)
      .attr('markerHeight', 6)
      .attr('orient', 'auto')
      .append('path')
      .attr('d', 'M0,-4L10,0L0,4Z')
      .attr('fill', HIGHLIGHT_COLOR);

    // Unhighlighted arrow for non-connected edges when a node is selected
    defs
      .append('marker')
      .attr('id', 'arrow-unhighlighted')
      .attr('viewBox', '0 -5 10 10')
      .attr('refX', 8)
      .attr('refY', 0)
      .attr('markerWidth', 6)
      .attr('markerHeight', 6)
      .attr('orient', 'auto')
      .append('path')
      .attr('d', 'M0,-4L10,0L0,4Z')
      .attr('fill', UNHIGHLIGHTED_COLOR);

    const edgeGroupsContainer = container.append('g').attr('class', 'edge-groups');
    edgeContainerRef.current = edgeGroupsContainer;

    const nodeCount = erdNodes.length;
    let linkDistance = 240;
    let chargeStrength = -1200;
    let collisionPadding = 30;

    if (nodeCount > 30) {
      linkDistance = 500;
      chargeStrength = -2500;
      collisionPadding = 50;
    } else if (nodeCount > 20) {
      linkDistance = 400;
      chargeStrength = -1800;
      collisionPadding = 40;
    } else if (nodeCount > 10) {
      linkDistance = 320;
      chargeStrength = -1500;
      collisionPadding = 35;
    }

    // Run simulation BEFORE rendering to calculate final positions
    const simulation = forceSimulation(nodes)
      .force(
        'link',
        forceLink(
          erdEdges.map((e) => ({
            source: nodes.find((node) => node.id === e.source)!,
            target: nodes.find((node) => node.id === e.target)!,
          })),
        )
          .distance(linkDistance)
          .strength(0.2),
      )
      .force('charge', forceManyBody().strength(chargeStrength).distanceMax(linkDistance * 2))
      .force('center', forceCenter(width / 2, height / 2).strength(0.08))
      .force('collision', forceCollide<D3Node>().radius((d) => {
        const dims = getNodeDimensions(d.label);
        return dims.radius + collisionPadding;
      }).strength(0.9).iterations(3))
      .force('x', forceX(width / 2).strength(0.05))
      .force('y', forceY(height / 2).strength(0.05));
      simulationRef.current = simulation;

    // Run simulation for better layout convergence
    const iterations = nodeCount > 20 ? 200 : 400;
    for (let i = 0; i < iterations; i++) {
      simulation.tick();
    }

    simulation.stop();

    // NOW render nodes with their final positions (no re-positioning needed)
    const nodeGroup = container.append('g').attr('class', 'nodes');
    nodeContainerRef.current = nodeGroup;

    const nodeElements = nodeGroup
      .selectAll('.node')
      .data(nodes)
      .enter()
      .append('g')
      .attr('class', 'node')
      .attr('transform', (d) => `translate(${d.x},${d.y})`) // Already in final position
      .style('cursor', 'grab')
      .on('click', function (event, d) {
        event.stopPropagation();
        setSelectedNodeId((prevId) => prevId === d.id ? null : d.id);
        setSelectedEdges([]);
      });

    if (mode === 'edit') {
      nodeElements.call(
        d3Drag<SVGGElement, D3Node>()
          .on('start', function(_event, d) {
            d.fx = d.x;
            d.fy = d.y;
            select(this).style('cursor', 'grabbing');
          })
          .on('drag', function(event, d) {
            d.fx = event.x;
            d.fy = event.y;
            d.x = event.x;
            d.y = event.y;
            select(this).attr('transform', `translate(${event.x},${event.y})`);
            renderEdgesOnly();
            onNodesChange?.(nodesRef.current);
            // Close any open badge tooltip modals when dragging nodes
            select('.badge-tooltip-popup').remove();
            select('body').on('click.badge-tooltip', null);
          })
          .on('end', function() {
            select(this).style('cursor', 'grab');
          }),
      );
    }

    nodeElements
      .append('rect')
      .attr('x', (d) => {
        const dims = getNodeDimensions(d.label);
        return -dims.width / 2;
      })
      .attr('y', (d) => {
        const dims = getNodeDimensions(d.label);
        return -dims.height / 2;
      })
      .attr('width', (d) => getNodeDimensions(d.label).width)
      .attr('height', (d) => getNodeDimensions(d.label).height)
      .attr('rx', 6)
      .attr('fill', (d) => d.background)
      .attr('stroke', getCSSVar('--erd-white') || '#fff')
      .attr('stroke-width', 2.5);

    nodeElements
      .append('foreignObject')
      .attr('x', (d) => {
        const dims = getNodeDimensions(d.label);
        return -dims.width / 2;
      })
      .attr('y', (d) => {
        const dims = getNodeDimensions(d.label);
        return -dims.height / 2;
      })
      .attr('width', (d) => getNodeDimensions(d.label).width)
      .attr('height', (d) => getNodeDimensions(d.label).height)
      .style('pointer-events', 'none')
      .append('xhtml:div')
      .style('width', '100%')
      .style('height', '100%')
      .style('display', 'flex')
      .style('align-items', 'center')
      .style('justify-content', 'center')
      .style('padding', '4px 8px')
      .style('text-align', 'center')
      .style('font-size', '13px')
      .style('font-weight', '700')
      .style('color', (d) => d.color)
      .style('word-break', 'break-word')
      .style('line-height', '1.2')
      .style('overflow-wrap', 'break-word')
      .text((d) => d.label);

    // Render edges after nodes are positioned
    renderEdgesOnly();

    // Fit to view immediately to prevent flicker
    handleResetZoom();

    // Save the initial transform state and node positions after zoom transition completes
    // D3 zoom transition typically takes ~250ms, so we wait for it to complete
    const saveInitialStateTimeout = setTimeout(() => {
      if (svgRef.current && initialNodePositionsRef.current.size === 0) {
        const currentTransform = zoomTransform(svgRef.current);
        initialTransformRef.current = currentTransform;

        // Save initial node positions
        nodes.forEach((node) => {
          initialNodePositionsRef.current.set(node.id, { x: node.x!, y: node.y! });
        });
      }
    }, 0);

    svg.on('click', () => {
      select('.badge-tooltip-popup').remove();
      setSelectedNodeId(null);
    });

    return () => {
      clearTimeout(saveInitialStateTimeout);
      simulation.stop();
    };
  }, [mode, debouncedDimensions, erdNodes, erdEdges, renderEdgesOnly, handleResetZoom]);

  const visibleEdgeCount = erdEdges.filter((edge) => {
    const sourceNode = erdNodes.find((node) => node.id === edge.source);
    const targetNode = erdNodes.find((node) => node.id === edge.target);
    return sourceNode && targetNode && visibleTypes.has(sourceNode.id) && visibleTypes.has(targetNode.id);
  }).length;

  const visibleNodeCount = erdNodes.filter((node) => visibleTypes.has(node.id)).length;

  // Memoize selected node to avoid repeated lookups
  const selectedNode = useMemo(() => {
    if (!selectedNodeId) { return null; }
    return nodesRef.current.find((node) => node.id === selectedNodeId);
  }, [selectedNodeId]);

  return (
    <div className="erd-container">
      <div ref={containerRef} className="erd-graph">
        <ZoomControls
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
          onResetZoom={handleResetZoom}
          onResetToInitial={handleResetToInitial}
        />
        <svg
          ref={svgRef}
          className="erd-svg"
          width={dimensions.width}
          height={dimensions.height}
        />
      </div>
    </div>
  );
};
